import type { SponsorsStyleAttribution } from './sponsorsStyleAttribution';
import type { EmailInteraction } from '../../../../supabase/functions/_shared/emailInteraction.ts';
import type { BrandThemeRecipeV2 } from '@/lib/embed/brandTheme';
import {
  pickEmailQuizStyleTokens,
  type EmailQuizStyleToken,
  type StyleOverrideFontFormat,
} from '@/lib/embed/styleOverrides';
import {
  deriveSponsorsStylePresetMatchedEmailTokens as deriveCanonicalMatchedEmailTokens,
  parsePublicSponsorsStylePresetConfiguration as parseCanonicalPublicConfiguration,
  parseSponsorsStylePresetConfiguration as parseCanonicalConfiguration,
  resolveSponsorsStylePresetEmailAppearance as resolveCanonicalEmailAppearance,
  type QuizSponsorsStylePresetResolution as CanonicalPresetResolution,
  type SponsorsStylePresetConfiguration as CanonicalPresetConfiguration,
} from '../../../../supabase/functions/_shared/sponsorsStylePresetResolver.ts';

export type SponsorsStylePresetId = string;

export interface SponsorsStylePresetFontAsset {
  family: string;
  format: StyleOverrideFontFormat;
  storageBucket: string;
  storagePath: string;
}

export interface SponsorsStylePresetEmailTemplate {
  preheader: string;
  heading: string;
  introText: string;
  footerText: string;
  ctaText: string;
  answerButtonStyle: 'solid' | 'outline';
}

export interface SponsorsStylePresetConfiguration {
  schemaVersion: 5;
  fontAssets: SponsorsStylePresetFontAsset[];
  web: {
    customStyleEnabled: boolean;
    themeMode: 'light' | 'dark' | 'device';
    themeRecipe: BrandThemeRecipeV2;
    leaderboardSignInEnabled: boolean;
  };
  email: {
    interaction?: EmailInteraction;
    styleSource: 'web' | 'custom';
    customStyleEnabled: boolean;
    styleTokens: Partial<Record<EmailQuizStyleToken, string>>;
    matchedStyleTokens: Partial<Record<EmailQuizStyleToken, string>>;
    template: SponsorsStylePresetEmailTemplate;
  };
  sponsorship: {
    sponsoredByEnabled: boolean;
    sponsoredByText: string;
    sponsoredByColor: string;
    footerUrl: string;
    logoStorageBucket: string | null;
    logoStoragePath: string | null;
    ctaEnabled: boolean;
    ctaImageStorageBucket: string | null;
    ctaImageStoragePath: string | null;
    ctaImageAspectRatio: number | null;
    ctaTagline: string;
    ctaTitle: string;
    ctaDescription: string;
    ctaButtonText: string;
    ctaUrl: string;
  };
  emailSignup: {
    enabled: boolean;
    signupType: 'button' | 'embed';
    title: string;
    description: string;
    buttonCta: string;
    confirmationMessage: string;
    signupUrl: string;
    embedCode: string;
  };
}

export interface SponsorsStylePresetEmailAppearance {
  interaction: EmailInteraction;
  customStyleEnabled: boolean;
  styleTokens: Partial<Record<EmailQuizStyleToken, string>>;
  fontAssets: SponsorsStylePresetFontAsset[];
  attribution: SponsorsStyleAttribution | null;
}

export function deriveSponsorsStylePresetMatchedEmailTokens(
  web: SponsorsStylePresetConfiguration['web']
): Partial<Record<EmailQuizStyleToken, string>> {
  return pickEmailQuizStyleTokens(
    deriveCanonicalMatchedEmailTokens(web as CanonicalPresetConfiguration['web'])
  );
}

export function resolveSponsorsStylePresetEmailAppearance(
  input: SponsorsStylePresetConfiguration | ResolvedQuizSponsorsStylePreset
): SponsorsStylePresetEmailAppearance {
  const appearance = resolveCanonicalEmailAppearance(
    input as CanonicalPresetConfiguration | CanonicalPresetResolution
  );
  return {
    ...appearance,
    styleTokens: pickEmailQuizStyleTokens(appearance.styleTokens),
  };
}

export type ResolvedQuizSponsorsStylePresetConfiguration = Omit<
  SponsorsStylePresetConfiguration,
  'email'
> &
  Partial<Pick<SponsorsStylePresetConfiguration, 'email'>>;

export interface SponsorsStylePresetRevision {
  id: string;
  revisionNumber: number;
  configuration: SponsorsStylePresetConfiguration;
  configurationHash: string;
  createdAt: string;
}

export interface SponsorsStylePresetSummary {
  id: SponsorsStylePresetId;
  slotNumber: number;
  name: string;
  usageCount: number;
  isAssigned: boolean;
  isDefault: boolean;
  currentRevision: SponsorsStylePresetRevision;
  createdAt: string;
  updatedAt: string;
}

export interface AtrivialDefaultSponsorsStylePreset {
  name: 'Atrivial Default';
  usageCount: number;
  isAssigned: boolean;
  isDefault: boolean;
  effectiveRevisionKey: string;
  configuration: SponsorsStylePresetConfiguration;
}

export interface SponsorsStylePresetWorkspace {
  partnerId: string;
  quizId: string;
  assignmentPresetId: SponsorsStylePresetId | null;
  defaultPresetId: SponsorsStylePresetId | null;
  plan: {
    tier: string;
    presetLimit: number | null;
    activePresetCount: number;
    canCreate: boolean;
    limitAction: 'upgrade' | 'contact_sales' | null;
  };
  presets: SponsorsStylePresetSummary[];
  atrivialDefault: AtrivialDefaultSponsorsStylePreset;
}

export interface SponsorsStylePresetOption {
  id: SponsorsStylePresetId | null;
  name: string;
  usageCount: number;
  isAssigned: boolean;
  isDefault: boolean;
  configuration: SponsorsStylePresetConfiguration;
  revisionId: string | null;
}

export type SponsorsStylePresetCommand =
  | {
      action: 'create_from_default';
      quizId: string;
      configuration: SponsorsStylePresetConfiguration;
    }
  | {
      action: 'duplicate';
      sourcePresetId: SponsorsStylePresetId;
      quizId: string;
    }
  | {
      action: 'revise';
      quizId: string;
      presetId: SponsorsStylePresetId;
      expectedRevisionId: string;
      configuration: SponsorsStylePresetConfiguration;
    }
  | {
      action: 'assign';
      quizId: string;
      presetId: SponsorsStylePresetId | null;
    }
  | {
      action: 'set_default';
      quizId: string;
      presetId: SponsorsStylePresetId | null;
    }
  | {
      action: 'delete_and_reassign';
      presetId: SponsorsStylePresetId;
      replacementPresetId: SponsorsStylePresetId | null;
    };

export interface SponsorsStylePresetCommandResult {
  commandId: string;
  action: SponsorsStylePresetCommand['action'];
  changedPresetId: SponsorsStylePresetId | null;
  workspace: SponsorsStylePresetWorkspace | null;
}

export interface ResolvedQuizSponsorsStylePreset {
  quizId: string;
  partnerId: string | null;
  presetId: SponsorsStylePresetId | null;
  revisionId: string | null;
  effectiveRevisionKey: string;
  configuration: ResolvedQuizSponsorsStylePresetConfiguration;
}

export function getSponsorsStyleAttribution(
  resolution: ResolvedQuizSponsorsStylePreset
): SponsorsStyleAttribution {
  return {
    presetId: resolution.presetId,
    revisionId: resolution.revisionId,
    effectiveRevisionKey: resolution.effectiveRevisionKey,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function record(value: unknown, label: string): Record<string, unknown> {
  if (!isRecord(value)) throw new Error(`Invalid ${label}`);
  return value;
}

function requiredString(value: unknown, label: string): string {
  if (typeof value !== 'string' || !value) throw new Error(`Invalid ${label}`);
  return value;
}

function nullableString(value: unknown): string | null {
  return typeof value === 'string' && value ? value : null;
}

function nonNegativeInteger(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : fallback;
}

function boolean(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function parseConfiguration(
  value: unknown,
  emailAccess: 'required' | 'public-redacted'
): ResolvedQuizSponsorsStylePresetConfiguration {
  if (emailAccess === 'required' || (isRecord(value) && isRecord(value.email))) {
    const configuration = parseCanonicalConfiguration(value);
    if (!configuration) throw new Error('Invalid Sponsors & Style preset configuration');
    return configuration as SponsorsStylePresetConfiguration;
  }

  const configuration = parseCanonicalPublicConfiguration(value);
  if (!configuration) throw new Error('Invalid Sponsors & Style preset configuration');
  return configuration as ResolvedQuizSponsorsStylePresetConfiguration;
}

export function parseSponsorsStylePresetConfiguration(
  value: unknown
): SponsorsStylePresetConfiguration {
  return parseConfiguration(value, 'required') as SponsorsStylePresetConfiguration;
}

function parsePresetSummary(value: unknown): SponsorsStylePresetSummary {
  const input = record(value, 'Sponsors & Style preset');
  const revision = record(input.currentRevision, 'Sponsors & Style preset revision');
  return {
    id: requiredString(input.id, 'Sponsors & Style preset id'),
    slotNumber: nonNegativeInteger(input.slotNumber),
    name: requiredString(input.name, 'Sponsors & Style preset name'),
    usageCount: nonNegativeInteger(input.usageCount),
    isAssigned: boolean(input.isAssigned),
    isDefault: boolean(input.isDefault),
    currentRevision: {
      id: requiredString(revision.id, 'Sponsors & Style preset revision id'),
      revisionNumber: nonNegativeInteger(revision.revisionNumber),
      configuration: parseSponsorsStylePresetConfiguration(revision.configuration),
      configurationHash: requiredString(
        revision.configurationHash,
        'Sponsors & Style preset configuration hash'
      ),
      createdAt: requiredString(revision.createdAt, 'Sponsors & Style preset revision createdAt'),
    },
    createdAt: requiredString(input.createdAt, 'Sponsors & Style preset createdAt'),
    updatedAt: requiredString(input.updatedAt, 'Sponsors & Style preset updatedAt'),
  };
}

export function parseSponsorsStylePresetWorkspace(value: unknown): SponsorsStylePresetWorkspace {
  const input = record(value, 'Sponsors & Style preset workspace');
  const plan = record(input.plan, 'Sponsors & Style preset plan');
  const atrivialDefault = record(input.atrivialDefault, 'Atrivial Default preset');
  const presets = Array.isArray(input.presets) ? input.presets.map(parsePresetSummary) : [];
  const presetLimit =
    plan.presetLimit === null ? null : nonNegativeInteger(plan.presetLimit, Number.NaN);

  if (presetLimit !== null && !Number.isInteger(presetLimit)) {
    throw new Error('Invalid Sponsors & Style preset limit');
  }

  return {
    partnerId: requiredString(input.partnerId, 'Sponsors & Style partner id'),
    quizId: requiredString(input.quizId, 'Sponsors & Style Quiz id'),
    assignmentPresetId: nullableString(input.assignmentPresetId),
    defaultPresetId: nullableString(input.defaultPresetId),
    plan: {
      tier: requiredString(plan.tier, 'Sponsors & Style plan tier'),
      presetLimit,
      activePresetCount: nonNegativeInteger(plan.activePresetCount),
      canCreate: boolean(plan.canCreate),
      limitAction:
        plan.limitAction === 'upgrade' || plan.limitAction === 'contact_sales'
          ? plan.limitAction
          : null,
    },
    presets,
    atrivialDefault: {
      name: 'Atrivial Default',
      usageCount: nonNegativeInteger(atrivialDefault.usageCount),
      isAssigned: boolean(atrivialDefault.isAssigned),
      isDefault: boolean(atrivialDefault.isDefault),
      effectiveRevisionKey: requiredString(
        atrivialDefault.effectiveRevisionKey,
        'Atrivial Default effective revision key'
      ),
      configuration: parseSponsorsStylePresetConfiguration(atrivialDefault.configuration),
    },
  };
}

export function parseSponsorsStylePresetCommandResult(
  value: unknown
): SponsorsStylePresetCommandResult {
  const input = record(value, 'Sponsors & Style preset command result');
  const action = input.action;
  if (
    action !== 'create_from_default' &&
    action !== 'duplicate' &&
    action !== 'revise' &&
    action !== 'assign' &&
    action !== 'set_default' &&
    action !== 'delete_and_reassign'
  ) {
    throw new Error('Invalid Sponsors & Style preset command action');
  }
  return {
    commandId: requiredString(input.commandId, 'Sponsors & Style preset command id'),
    action,
    changedPresetId: nullableString(input.changedPresetId),
    workspace:
      isRecord(input.workspace) &&
      typeof input.workspace.quizId === 'string' &&
      Boolean(input.workspace.quizId)
        ? parseSponsorsStylePresetWorkspace(input.workspace)
        : null,
  };
}

export function parseResolvedQuizSponsorsStylePreset(
  value: unknown
): ResolvedQuizSponsorsStylePreset {
  const input = record(value, 'resolved Quiz Sponsors & Style preset');
  return {
    quizId: requiredString(input.quizId, 'resolved Sponsors & Style Quiz id'),
    partnerId: nullableString(input.partnerId),
    presetId: nullableString(input.presetId),
    revisionId: nullableString(input.revisionId),
    effectiveRevisionKey: requiredString(
      input.effectiveRevisionKey,
      'resolved Sponsors & Style effective revision key'
    ),
    configuration: parseConfiguration(input.configuration, 'public-redacted'),
  };
}

export function getSponsorsStylePresetOptions(
  workspace: SponsorsStylePresetWorkspace
): SponsorsStylePresetOption[] {
  const customOptions = [...workspace.presets]
    .sort((left, right) => left.slotNumber - right.slotNumber)
    .map((preset) => ({
      id: preset.id,
      name: preset.name,
      usageCount: preset.usageCount,
      isAssigned: preset.isAssigned,
      isDefault: preset.isDefault,
      configuration: preset.currentRevision.configuration,
      revisionId: preset.currentRevision.id,
    }));

  return [
    ...customOptions,
    {
      id: null,
      name: workspace.atrivialDefault.name,
      usageCount: workspace.atrivialDefault.usageCount,
      isAssigned: workspace.atrivialDefault.isAssigned,
      isDefault: workspace.atrivialDefault.isDefault,
      configuration: workspace.atrivialDefault.configuration,
      revisionId: null,
    },
  ];
}
